import { takeEvery, all, put, call, select } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'
import { RequestStatus } from 'features/common'
import {
  completeOnboardingChecklistRequest,
  getOnboardingChecklistRequest,
  setOnboardingChecklist,
  setOnboardingChecklistStatus,
  setOnboardingChecklistError,
  setOnboardingChecklistUpdateStatus,
  setOnboardingChecklistUpdateError,
  showOnboardingChecklistCompletionConfirmation,
  OnboardingChecklistState
} from './onboardingChecklistSlice'
import { createApiError } from 'features/common/api/apiError'

import { getOnboardingChecklist, completeOnboardingChecklist } from '../api/onboardingChecklist'
import { createResource, getResourceStateful } from 'app/state/resource'
import { onboardingItemCheckedEventRequest } from 'features/metrics/state/metricsSlice'
import { MetricEventType } from 'features/metrics/api/metrics'
import { createMetricRequest } from 'features/metrics/state/metricsSlice'
import { isEditor } from 'features/users/model/user'
import { OnboardingAction } from '../utils/useOnboardingChecklist'

const videoTaskIds = [
  OnboardingAction.WatchWorkspaceVideo,
  OnboardingAction.WatchWorkflowsVideo,
  OnboardingAction.WatchBuilderVideo,
  OnboardingAction.WatchAIAgentVideo
]

function filterOnboardingItems(onboardingChecklist, isUserEditor, hasCDW) {
  const filteredTasks = onboardingChecklist.tasks
    .filter((task) => !videoTaskIds.includes(task.id))
    .filter((task) => !isUserEditor || task.id !== OnboardingAction.InviteColleagues)
    .filter((task) => hasCDW || task.id !== OnboardingAction.CheckDemoMap)

  return {
    completed: filteredTasks.every((task) => task.completed),
    videos: onboardingChecklist.tasks.filter((task) => videoTaskIds.includes(task.id)),
    tasks: filteredTasks
  }
}

function* fetchOnboardingChecklist() {
  try {
    yield put(setOnboardingChecklistStatus(RequestStatus.Pending))
    const onboardingChecklist = yield call(getOnboardingChecklist)
    const user = yield select((state) => state.users.me)
    const hasCDW = yield select((state) => !state.account.data.hideCartoDatawarehouse)
    const isUserEditor = isEditor(user)

    const filteredOnboardingItems = yield call(filterOnboardingItems, onboardingChecklist, isUserEditor, hasCDW)

    yield put(setOnboardingChecklist(filteredOnboardingItems))
    yield put(setOnboardingChecklistStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setOnboardingChecklistStatus(RequestStatus.Rejected))
    yield put(setOnboardingChecklistError(createApiError(e)))
  }
}

export const onboardingChecklistResource = createResource({
  get: (state) => state.onboardingChecklist,
  status: (state) => state.onboardingChecklist.status,
  createAction: getOnboardingChecklistRequest
})

function filterOutCompletedTasks(tasksIds: string[], onboardingChecklist: OnboardingChecklistState) {
  return tasksIds?.filter((taskId) => {
    const isCompleted = onboardingChecklist.tasks?.find((task) => task.id === taskId)?.completed
    return !isCompleted
  })
}

function* updateOnboardingChecklist(action: PayloadAction<'SkipOnBoarding' | string[]>) {
  try {
    const currentOnboardingCheckList: OnboardingChecklistState = yield call(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      getResourceStateful,
      onboardingChecklistResource
    )
    const skipOnBoarding = action.payload === 'SkipOnBoarding'
    const tasks =
      action.payload === 'SkipOnBoarding'
        ? (currentOnboardingCheckList.tasks || []).map((task) => task.id)
        : action.payload

    const tasksToComplete = filterOutCompletedTasks(tasks, currentOnboardingCheckList)
    if (!tasksToComplete?.length) return

    yield put(setOnboardingChecklistUpdateStatus(RequestStatus.Pending))
    const onboardingChecklist = yield call(completeOnboardingChecklist, tasksToComplete)

    const user = yield select((state) => state.users.me)
    const isUserEditor = isEditor(user)
    const hasCDW = yield select((state) => !state.account.data.hideCartoDatawarehouse)

    const filteredOnboardingItems = yield call(filterOnboardingItems, onboardingChecklist, isUserEditor, hasCDW)
    const allTasksCompleted = filteredOnboardingItems.tasks.every((task) => task.completed)

    if (allTasksCompleted && !skipOnBoarding) {
      yield put(showOnboardingChecklistCompletionConfirmation(true))
    }
    yield put(setOnboardingChecklist(filteredOnboardingItems))
    yield put(setOnboardingChecklistUpdateStatus(RequestStatus.Fulfilled))

    if (skipOnBoarding) {
      yield put(createMetricRequest(MetricEventType.OnboardingSkipped))
    } else {
      for (const taskId of tasksToComplete) {
        yield put(onboardingItemCheckedEventRequest({ item: taskId }))
      }
    }
  } catch (e) {
    yield put(setOnboardingChecklistUpdateStatus(RequestStatus.Rejected))
    yield put(setOnboardingChecklistUpdateError(createApiError(e)))
  }
}

function* watchFetchOnboardingChecklist() {
  yield takeEvery(completeOnboardingChecklistRequest.type, updateOnboardingChecklist)
  yield takeEvery(getOnboardingChecklistRequest.type, fetchOnboardingChecklist)
}

export default function* onboardingChecklistSaga() {
  try {
    yield all([watchFetchOnboardingChecklist()])
  } catch (e) {
    console.error(e)
  }
}
